import React, { useState } from 'react';
import {
  Briefcase, Search, Plus, MoreHorizontal, Users,
  Calendar, TrendingUp, CheckCircle2, XCircle,
  Clock, AlertCircle, ChevronRight, ArrowUpRight,
  Edit2, Copy, EyeOff, BarChart2, MapPin, Layers
} from 'lucide-react';
import { APP, IconPill } from './DashbordApplicationTab';
import { useNavigate } from 'react-router-dom';

/* ─── Helpers ────────────────────────────────────────────────────────────── */
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const daysAgo = (d) => {
  if (!d) return null;
  const diff = Math.floor((Date.now() - new Date(d)) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return '1 day ago';
  return `${diff} days ago`;
};

/* ─── Job status badge ───────────────────────────────────────────────────── */
const JobStatusBadge = ({ status }) => {
  const map = {
    active:  { bg: APP.greenBg,   color: APP.green,   label: 'Active'  },
    closed:  { bg: APP.redBg,     color: APP.red,     label: 'Closed'  },
    draft:   { bg: APP.amberBg,   color: APP.amber,   label: 'Draft'   },
    paused:  { bg: APP.skyBg,     color: APP.sky,     label: 'Paused'  },
  };
  const s = map[status] ?? map.active;
  return (
    <span
      className="text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize"
      style={{ backgroundColor: s.bg, color: s.color }}
    >
      {s.label}
    </span>
  );
};

/* ─── KPI summary cards ──────────────────────────────────────────────────── */
const JobsSummaryCards = ({ jobs, appsByJob }) => {
  const total      = jobs.length;
  const totalApps  = appsByJob.reduce((s, j) => s + (j.applicationCount || 0), 0);
  const topJob     = [...appsByJob].sort((a, b) => b.applicationCount - a.applicationCount)[0];
  const avgApps    = total ? (totalApps / total).toFixed(1) : '0';

  const CARDS = [
    { label: 'Total jobs posted', value: total,        sub: 'All time',           icon: Briefcase,     bg: APP.primaryBg, color: APP.primary },
    { label: 'Total applications', value: totalApps,   sub: `${avgApps} per job`, icon: Users,         bg: APP.purpleBg,  color: APP.purple  },
    { label: 'Top performing job', value: topJob?.jobTitle?.split(' ').slice(0,2).join(' ') || '—',
                                                        sub: `${topJob?.applicationCount ?? 0} applications`,
                                                                                   icon: TrendingUp,    bg: APP.greenBg,   color: APP.green   },
    { label: 'Avg. apps / job',   value: avgApps,      sub: 'Across all postings', icon: BarChart2,    bg: APP.amberBg,   color: APP.amber   },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
      {CARDS.map(({ label, value, sub, icon, bg, color }) => (
        <div
          key={label}
          style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
          className="rounded-2xl p-4 flex flex-col gap-3 hover:shadow-sm transition-shadow"
        >
          <IconPill icon={icon} bg={bg} color={color} />
          <div>
            <p style={{ color: APP.text }} className="text-2xl font-bold leading-none truncate">{value}</p>
            <p style={{ color: APP.textSm }} className="text-xs mt-1">{label}</p>
          </div>
          <p style={{ color: APP.textXs }} className="text-xs">{sub}</p>
        </div>
      ))}
    </div>
  );
};

/* ─── Job card (grid view) ───────────────────────────────────────────────── */
const JobCard = ({ job, appCount, onAction, navigate }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div
      style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
      className="rounded-2xl p-4 flex flex-col gap-3 hover:shadow-sm transition-shadow relative group"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div
          style={{ backgroundColor: APP.primaryBg }}
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        >
          <Briefcase size={16} style={{ color: APP.primary }} />
        </div>

        {/* More menu */}
        <div className="relative">
          <button
            onClick={() => setMenuOpen((p) => !p)}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg hover:bg-slate-100"
            style={{ color: APP.textSm }}
          >
            <MoreHorizontal size={14} />
          </button>
          {menuOpen && (
            <div
              style={{
                backgroundColor: APP.card,
                border: `1px solid ${APP.border}`,
                boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
              }}
              className="absolute right-0 top-7 z-20 rounded-xl py-1 min-w-[160px]"
            >
              {[
                { label: 'Edit job',      icon: Edit2,    action: () => navigate(`/admin/jobs/${job._id}/edit`) },
                { label: 'Duplicate',     icon: Copy,     action: () => onAction(job._id, 'duplicate') },
                { label: 'Close job',     icon: EyeOff,   action: () => onAction(job._id, 'close') },
              ].map(({ label, icon: Icon, action }) => (
                <button
                  key={label}
                  onClick={() => { action(); setMenuOpen(false); }}
                  style={{ color: APP.textMd }}
                  className="w-full text-left px-3 py-2 text-xs font-medium hover:bg-slate-50 transition-colors flex items-center gap-2"
                >
                  <Icon size={12} /> {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Title & location */}
      <div>
        <p style={{ color: APP.text }} className="text-sm font-semibold leading-snug line-clamp-2">
          {job.title}
        </p>
        {job.location && (
          <p style={{ color: APP.textXs }} className="text-[11px] mt-1 flex items-center gap-1">
            <MapPin size={10} /> {job.location}
          </p>
        )}
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1">
          <Users size={11} style={{ color: APP.textXs }} />
          <span style={{ color: APP.textSm }} className="text-xs font-semibold">{appCount}</span>
          <span style={{ color: APP.textXs }} className="text-[10px]">apps</span>
        </div>
        <div className="flex items-center gap-1">
          <Calendar size={11} style={{ color: APP.textXs }} />
          <span style={{ color: APP.textXs }} className="text-[10px]">{daysAgo(job.createdAt)}</span>
        </div>
      </div>

      {/* Footer */}
      <div
        className="flex items-center justify-between pt-2"
        style={{ borderTop: `1px solid ${APP.borderLight}` }}
      >
        <JobStatusBadge status={job.status ?? 'active'} />
        <button
          onClick={() => navigate(`/admin/jobs/${job._id}/applicants`)}
          style={{ color: APP.primary }}
          className="text-[10px] font-semibold flex items-center gap-0.5 hover:underline"
        >
          View applicants <ChevronRight size={10} />
        </button>
      </div>
    </div>
  );
};

/* ─── Job list row (list view) ───────────────────────────────────────────── */
const JobListRow = ({ job, appCount, onAction, navigate }) => (
  <div
    style={{ borderBottom: `1px solid ${APP.borderLight}` }}
    className="flex items-center gap-3 py-3 px-4 hover:bg-slate-50 transition-colors group"
  >
    <div
      style={{ backgroundColor: APP.primaryBg }}
      className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
    >
      <Briefcase size={14} style={{ color: APP.primary }} />
    </div>

    {/* Title */}
    <div className="flex-1 min-w-0">
      <p style={{ color: APP.text }} className="text-sm font-medium truncate">{job.title}</p>
      <p style={{ color: APP.textXs }} className="text-[11px] flex items-center gap-1 mt-0.5">
        {job.location && <><MapPin size={9} />{job.location} · </>}
        {fmtDate(job.createdAt)}
      </p>
    </div>

    {/* Apps count */}
    <div className="hidden sm:flex items-center gap-1 min-w-[60px]">
      <Users size={12} style={{ color: APP.textXs }} />
      <span style={{ color: APP.text }} className="text-sm font-bold">{appCount}</span>
      <span style={{ color: APP.textXs }} className="text-xs">apps</span>
    </div>

    <JobStatusBadge status={job.status ?? 'active'} />

    {/* Actions */}
    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
      <button
        onClick={() => navigate(`/admin/jobs/${job._id}/applicants`)}
        title="View applicants"
        style={{ backgroundColor: APP.primaryBg, color: APP.primary }}
        className="w-7 h-7 rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity"
      >
        <Users size={12} />
      </button>
      <button
        onClick={() => navigate(`/admin/jobs/${job._id}/edit`)}
        title="Edit"
        style={{ backgroundColor: APP.skyBg, color: APP.sky }}
        className="w-7 h-7 rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity"
      >
        <Edit2 size={12} />
      </button>
    </div>
  </div>
);

/* ─── Empty state ────────────────────────────────────────────────────────── */
const EmptyJobs = ({ navigate }) => (
  <div className="flex flex-col items-center gap-3 py-16">
    <div
      style={{ backgroundColor: APP.primaryBg }}
      className="w-14 h-14 rounded-2xl flex items-center justify-center"
    >
      <Briefcase size={22} style={{ color: APP.primary }} />
    </div>
    <p style={{ color: APP.text }} className="text-sm font-semibold">No jobs found</p>
    <p style={{ color: APP.textXs }} className="text-xs text-center max-w-xs">
      Try adjusting your search or filters, or post a new job to get started.
    </p>
    <button
      onClick={() => navigate('/admin/jobs/post')}
      style={{ backgroundColor: APP.primary }}
      className="mt-1 inline-flex items-center gap-2 px-4 py-2 text-white text-xs font-semibold rounded-xl hover:opacity-90 transition-opacity"
    >
      <Plus size={13} /> Post a Job
    </button>
  </div>
);

/* ─── Main JobsTab ───────────────────────────────────────────────────────── */
const JobsTab = ({ dashboardData }) => {
  const [view, setView]           = useState('grid');   // 'grid' | 'list'
  const [search, setSearch]       = useState('');
  const [sortBy, setSortBy]       = useState('newest'); // 'newest' | 'most_apps' | 'title'
  const navigate = useNavigate();

  /* Build a jobs list from dashboardData.
     applicationsByJob has: { jobId, jobTitle, applicationCount }
     We enrich it with a synthetic job object so this works even if you
     don't have a separate /jobs endpoint on the dashboard yet. */
  const rawJobs = (dashboardData?.applicationsByJob || []).map((j) => ({
    _id:            j.jobId   ?? j._id ?? j.jobTitle,
    title:          j.jobTitle,
    location:       j.location ?? null,
    createdAt:      j.createdAt ?? null,
    status:         j.status   ?? 'active',
    applicationCount: j.applicationCount ?? 0,
  }));

  /* Filter */
  const filtered = rawJobs.filter((j) =>
    !search || j.title?.toLowerCase().includes(search.toLowerCase())
  );

  /* Sort */
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'most_apps') return b.applicationCount - a.applicationCount;
    if (sortBy === 'title')     return a.title?.localeCompare(b.title);
    // newest — fall back to index order (API already returns newest first typically)
    return 0;
  });

  const handleAction = (jobId, action) => {
    // Hook your API here: close / duplicate / etc.
    console.log('Job action:', action, jobId);
  };

  const appLookup = (jobId) =>
    rawJobs.find((j) => j._id === jobId)?.applicationCount ?? 0;

  return (
    <div>
      {/* Section header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h2 style={{ color: APP.text }} className="text-base font-semibold">
            Job Postings
          </h2>
          <p style={{ color: APP.textXs }} className="text-xs mt-0.5">
            {rawJobs.length} job{rawJobs.length !== 1 ? 's' : ''} posted in total
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* View toggle */}
          <div style={{ border: `1px solid ${APP.border}` }} className="flex rounded-xl overflow-hidden">
            {[
              { key: 'grid', label: 'Grid' },
              { key: 'list', label: 'List' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setView(key)}
                style={
                  view === key
                    ? { backgroundColor: APP.primary, color: '#fff' }
                    : { backgroundColor: APP.card, color: APP.textMd }
                }
                className="px-4 py-1.5 text-xs font-medium hover:opacity-90 transition-opacity"
              >
                {label}
              </button>
            ))}
          </div>

          {/* Post new job */}
          <button
            onClick={() => navigate('/admin/jobs/post')}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white text-xs font-bold rounded-xl shadow-sm hover:-translate-y-0.5 active:translate-y-0 transition-all"
          >
            <Plus size={13} strokeWidth={2.5} /> Post New Job
          </button>
        </div>
      </div>

      {/* Summary KPI cards */}
      <JobsSummaryCards jobs={rawJobs} appsByJob={rawJobs} />

      {/* Filter / sort bar */}
      <div
        style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
        className="rounded-2xl px-4 py-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center mb-5"
      >
        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <Search
            size={13}
            className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: APP.textXs }}
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search job title…"
            style={{
              backgroundColor: APP.bg,
              border: `1px solid ${APP.border}`,
              color: APP.text,
            }}
            className="w-full pl-8 pr-3 py-2 text-xs rounded-xl outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        {/* Sort pills */}
        <div className="flex gap-1.5 flex-wrap">
          {[
            { key: 'newest',    label: 'Newest first'  },
            { key: 'most_apps', label: 'Most applied'  },
            { key: 'title',     label: 'A → Z'         },
          ].map(({ key, label }) => {
            const active = sortBy === key;
            return (
              <button
                key={key}
                onClick={() => setSortBy(key)}
                style={
                  active
                    ? { backgroundColor: APP.primaryBg, color: APP.primary, border: `1px solid ${APP.primary}22` }
                    : { backgroundColor: APP.bg, color: APP.textMd, border: `1px solid ${APP.border}` }
                }
                className="px-3 py-1.5 text-xs font-medium rounded-lg hover:opacity-90 transition-opacity"
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Grid view ── */}
      {view === 'grid' && (
        sorted.length === 0
          ? <EmptyJobs navigate={navigate} />
          : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {sorted.map((job) => (
                <JobCard
                  key={job._id}
                  job={job}
                  appCount={job.applicationCount}
                  onAction={handleAction}
                  navigate={navigate}
                />
              ))}
            </div>
          )
      )}

      {/* ── List view ── */}
      {view === 'list' && (
        <div
          style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
          className="rounded-2xl overflow-hidden"
        >
          {/* Table head */}
          <div
            className="flex items-center gap-3 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider"
            style={{
              color: APP.textXs,
              borderBottom: `1px solid ${APP.border}`,
              backgroundColor: APP.bg,
            }}
          >
            <span className="w-9 flex-shrink-0" />
            <span className="flex-1">Job Title</span>
            <span className="hidden sm:block w-20">Applications</span>
            <span className="w-16">Status</span>
            <span className="w-16 opacity-0">Actions</span>
          </div>

          {sorted.length === 0 ? (
            <EmptyJobs navigate={navigate} />
          ) : (
            sorted.map((job) => (
              <JobListRow
                key={job._id}
                job={job}
                appCount={job.applicationCount}
                onAction={handleAction}
                navigate={navigate}
              />
            ))
          )}
        </div>
      )}

      {/* Bottom insight banner */}
      {sorted.length > 0 && (
        <div
          style={{ backgroundColor: APP.primaryBg, border: `1px solid ${APP.primary}22` }}
          className="rounded-2xl px-4 py-3 mt-5 flex items-center gap-3"
        >
          <TrendingUp size={15} style={{ color: APP.primary }} />
          <p style={{ color: APP.primary }} className="text-xs font-medium flex-1">
            Your top job has{' '}
            <span className="font-bold">
              {[...rawJobs].sort((a, b) => b.applicationCount - a.applicationCount)[0]?.applicationCount ?? 0}
            </span>{' '}
            applications —{' '}
            <span className="font-bold">
              {[...rawJobs].sort((a, b) => b.applicationCount - a.applicationCount)[0]?.title ?? '—'}
            </span>
          </p>
          <button
            onClick={() => setSortBy('most_apps')}
            style={{ color: APP.primary }}
            className="text-xs font-semibold flex items-center gap-1 hover:underline whitespace-nowrap"
          >
            Sort by most applied <ChevronRight size={12} />
          </button>
        </div>
      )}
    </div>
  );
};

export default JobsTab;